// Creator home — unified back-office dashboard. 2026-07-28.
// Replaces /portal as the default creator landing (via Dashboard.jsx router).
// One-request aggregate → GET /api/me/dashboard.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../auth/AuthContext";
import { BrandFooter } from "../components/brand/Footer";
import { LogoLockup } from "../components/brand/LogoLockup";

const STATUS_PILL = {
  received:          { label: "Application received",   cls: "border-brand-gold/40 text-brand-gold" },
  approved:          { label: "Approved · Onboarding",  cls: "border-emerald-400/40 text-emerald-300" },
  rejected:          { label: "Not selected",           cls: "border-rose-300/40 text-rose-300" },
  changes_requested: { label: "Changes requested",      cls: "border-sky-300/40 text-sky-300" },
  archived:          { label: "Archived",               cls: "border-brand-text/25 text-brand-text/60" },
};

const CONTRACT_UI = {
  not_assigned: { label: "Awaiting Assignment", cls: "border-brand-text/25 text-brand-text/60",
                  body: "Your management contract will be prepared by leadership once your application review is complete." },
  draft:        { label: "Under Review",        cls: "border-brand-text/25 text-brand-text/60",
                  body: "Leadership is finalising the terms of your agreement. You'll receive an email the moment it's ready to sign." },
  sent:         { label: "Ready to Sign",       cls: "border-brand-gold/40 text-brand-gold",
                  body: "Your contract has been emailed to you. Review, sign, and return the signed copy." },
  signed:       { label: "Signed",              cls: "border-emerald-400/40 text-emerald-300",
                  body: "We've received your signed copy. Activation will be confirmed by leadership." },
  active:       { label: "Active",              cls: "border-emerald-400/40 text-emerald-300",
                  body: "Your management agreement is active. Welcome to the roster." },
  expired:      { label: "Expired",             cls: "border-brand-text/30 text-brand-text/60",
                  body: "Your contract term has ended. Reach out to leadership about renewal." },
  terminated:   { label: "Terminated",          cls: "border-rose-400/40 text-rose-300",
                  body: "Your contract has been terminated. Reach out to leadership if you have questions." },
};

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return null; }
}

function CreatorNav({ onLogout, stageName }) {
  return (
    <header data-testid="creator-nav" className="border-b border-brand-text/10 bg-brand-bg/80 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-6">
          <LogoLockup variant="header" as={Link} to="/creator" />
          <nav className="hidden gap-6 text-[10px] uppercase tracking-[0.28em] text-brand-text/50 md:flex">
            <Link data-testid="nav-dashboard" to="/creator" className="hover:text-brand-gold">Dashboard</Link>
            <Link data-testid="nav-learning" to="/creator/learning" className="hover:text-brand-gold">Learning</Link>
            <Link data-testid="nav-documents" to="/creator/documents" className="hover:text-brand-gold">Documents</Link>
            <Link data-testid="nav-profile"   to="/profile" className="hover:text-brand-gold">My Profile</Link>
            <Link data-testid="nav-contract"  to="/contract" className="hover:text-brand-gold">My Contract</Link>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="notification-bell"
            aria-label="Notifications"
            className="relative rounded-full border border-brand-text/15 p-2 text-brand-text/60 transition hover:border-brand-gold/40 hover:text-brand-gold"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
          </button>
          <button
            data-testid="nav-signout"
            onClick={onLogout}
            className="hidden rounded-full border border-brand-text/15 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-brand-text/60 hover:border-brand-gold/40 hover:text-brand-gold md:inline-flex"
          >
            Sign out
          </button>
        </div>
      </div>
      {/* Mobile stage-name banner + inline nav */}
      <div className="mx-auto flex max-w-5xl flex-col gap-2 px-6 pb-3 md:hidden">
        <div className="flex gap-4 text-[10px] uppercase tracking-[0.28em] text-brand-text/50">
          <Link data-testid="nav-dashboard-mobile" to="/creator" className="hover:text-brand-gold">Dashboard</Link>
          <Link data-testid="nav-learning-mobile"  to="/creator/learning" className="hover:text-brand-gold">Learning</Link>
          <Link data-testid="nav-profile-mobile"   to="/profile" className="hover:text-brand-gold">Profile</Link>
          <Link data-testid="nav-contract-mobile"  to="/contract" className="hover:text-brand-gold">Contract</Link>
          <button data-testid="nav-signout-mobile" onClick={onLogout} className="ml-auto hover:text-brand-gold">Sign out</button>
        </div>
      </div>
    </header>
  );
}

function JourneyTimeline({ nodes }) {
  return (
    <div data-testid="journey-timeline" className="rounded-2xl border border-brand-text/10 bg-[#141414] p-6 sm:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Your journey</p>
      <ol className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-5 sm:gap-2">
        {nodes.map((n, idx) => {
          const isDone = n.state === "done";
          const isActive = n.state === "active";
          const dot = isDone
            ? "border-emerald-400/60 bg-emerald-400/20 text-emerald-300"
            : isActive
              ? "border-brand-gold/60 bg-brand-gold/20 text-brand-gold"
              : "border-brand-text/15 bg-transparent text-brand-text/40";
          const label = isDone ? "text-brand-text" : isActive ? "text-brand-text" : "text-brand-text/40";
          return (
            <li
              key={n.key}
              data-testid={`journey-node-${n.key}`}
              data-state={n.state}
              className="relative flex items-start gap-3 sm:flex-col sm:items-start"
            >
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-display text-sm ${dot}`}>
                {isDone ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  String(idx + 1).padStart(2, "0")
                )}
              </div>
              <div className="flex-1">
                <div className={`text-[11px] uppercase tracking-[0.24em] ${label}`}>{n.label}</div>
                {n.at && <div className="mt-1 font-mono text-[10px] text-brand-text/40">{fmt(n.at)}</div>}
                {isActive && <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-gold">Up next</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ProfileCard({ profile }) {
  const pct = profile?.percent ?? 0;
  const next = profile?.next_section;
  const isComplete = pct >= 100;
  return (
    <div data-testid="profile-card" className="rounded-2xl border border-brand-text/10 bg-[#141414] p-6 sm:p-8">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Your profile</p>
        <p data-testid="profile-card-percent" className="font-display text-3xl text-brand-text">{pct}%</p>
      </div>
      <div className="mt-4 h-1 w-full overflow-hidden rounded bg-brand-text/10">
        <div
          data-testid="profile-card-bar"
          className="h-full bg-gradient-to-r from-brand-gold/70 to-brand-gold transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p data-testid="profile-card-summary" className="mt-3 text-[11px] uppercase tracking-[0.18em] text-brand-text/45">
        {profile?.sections_complete ?? 0} of {profile?.sections_visible ?? 0} sections complete
      </p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-brand-text/70">
          {isComplete
            ? "Your profile is complete. You can still update details anytime."
            : next
              ? <>Pick up where you left off — <span className="text-brand-gold">Section {next.n} · {next.label}</span>.</>
              : "Your profile is ready to build."}
        </div>
        <Link
          data-testid="profile-card-cta"
          to={next ? `/profile#section-${next.n}` : "/profile"}
          className="inline-block rounded-full border border-brand-gold bg-brand-gold/[0.04] px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-gold transition hover:bg-brand-gold hover:text-brand-bg"
        >
          {isComplete ? "View profile" : "Continue"}
        </Link>
      </div>
    </div>
  );
}

function ContractCard({ contract }) {
  const status = contract?.status || "not_assigned";
  const ui = CONTRACT_UI[status] || CONTRACT_UI.not_assigned;
  const pct = contract?.agreed_percentage;
  const showSignCta = status === "sent" && contract?.docusign_ready;
  const showDocuSignNotice = status === "sent" && !contract?.docusign_ready;
  const showDownload = contract?.has_pdf && (status === "sent" || status === "signed" || status === "active");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="contract-card" data-status={status} className="rounded-2xl border border-brand-text/10 bg-[#141414] p-6 sm:p-8">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Your contract</p>
        <span data-testid="contract-card-status" className={`rounded-full border ${ui.cls} px-3 py-1 text-[10px] uppercase tracking-[0.24em]`}>{ui.label}</span>
      </div>
      {contract?.label && <p className="mt-3 font-display text-xl text-brand-text">{contract.label}</p>}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/45">Agreed rate</p>
          {pct != null ? (
            <p data-testid="contract-card-percentage" className="mt-1 font-display text-2xl text-brand-text">
              {Number(pct) % 1 === 0
                ? `${Number(pct)}%`
                : `${Number(pct).toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}%`}
            </p>
          ) : (
            <p data-testid="contract-card-percentage-pending" className="mt-1 text-sm italic text-brand-text/60">Pending review</p>
          )}
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-text/45">Type</p>
          <p className="mt-1 text-sm text-brand-text">{contract?.type || "—"}</p>
        </div>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-brand-text/70">{ui.body}</p>

      {showDocuSignNotice && (
        <div data-testid="contract-card-docusign-notice" className="mt-4 rounded border border-brand-gold/25 bg-brand-gold/[0.04] px-4 py-3 text-xs text-brand-text/70">
          E-signature setup is pending. Once leadership finalises DocuSign, you'll receive a signing link by email — no action needed here yet.
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {showSignCta && (
          <a
            data-testid="contract-card-sign-cta"
            href={contract?.docusign_signing_url || "#docusign-pending"}
            className="rounded-full border border-brand-gold bg-brand-gold px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-bg transition hover:bg-brand-bg hover:text-brand-gold"
          >
            Sign contract
          </a>
        )}
        {showDownload && (
          <a
            data-testid="contract-card-download"
            href={`${BACKEND}/api/me/contract/pdf`}
            target="_blank" rel="noreferrer"
            className="rounded-full border border-brand-gold/60 px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg"
          >
            Download contract
          </a>
        )}
        <Link
          data-testid="contract-card-details"
          to="/contract"
          className="rounded-full border border-brand-text/15 px-5 py-2 text-[11px] uppercase tracking-[0.28em] text-brand-text/60 hover:border-brand-gold/40 hover:text-brand-gold"
        >
          Full details
        </Link>
      </div>
    </div>
  );
}

function SupportCard({ email }) {
  return (
    <div data-testid="support-card" className="rounded-2xl border border-brand-text/10 bg-[#141414] p-6 sm:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Need a hand?</p>
      <p className="mt-3 text-sm text-brand-text/70">
        Reach out anytime — leadership responds within 48 hours.
      </p>
      <a
        data-testid="support-email-link"
        href={`mailto:${email}`}
        className="mt-4 inline-block font-mono text-sm text-brand-gold hover:underline"
      >
        {email}
      </a>
    </div>
  );
}

export default function CreatorHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => { document.title = "Dashboard · Omertàbella"; }, []);

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/me/dashboard")
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, data: null, error: e?.response?.status === 401 ? "unauthorized" : "fetch_failed" });
      });
    return () => { cancelled = true; };
  }, []);

  const onSignOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (state.loading) {
    return (
      <main className="min-h-screen bg-brand-bg text-brand-text">
        <CreatorNav onLogout={onSignOut} stageName={user?.name} />
        <div data-testid="creator-home-loading" className="mx-auto max-w-5xl px-6 py-24 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45">
          Loading your dashboard…
        </div>
      </main>
    );
  }

  if (state.error || !state.data) {
    return (
      <main className="min-h-screen bg-brand-bg text-brand-text">
        <CreatorNav onLogout={onSignOut} stageName={user?.name} />
        <div data-testid="creator-home-error" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-2xl text-brand-text">Couldn't load your dashboard.</h1>
          <p className="mt-3 text-sm text-brand-text/60">Please try again in a moment, or email <a className="text-brand-gold" href="mailto:admin@omertabella.com">admin@omertabella.com</a>.</p>
        </div>
      </main>
    );
  }

  const d = state.data;
  const appStatus = d.application?.status;
  const statusPill = appStatus ? STATUS_PILL[appStatus] : null;

  return (
    <main data-testid="creator-home" className="min-h-screen bg-brand-bg text-brand-text">
      <CreatorNav onLogout={onSignOut} stageName={d.user?.stage_name} />

      <div className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        {/* Header */}
        <section data-testid="creator-header">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Welcome back</p>
          <h1 data-testid="creator-header-name" className="mt-3 font-display text-4xl text-brand-text sm:text-5xl lg:text-6xl">
            {d.user?.stage_name || "Creator"}<span className="text-brand-gold">.</span>
          </h1>
          {statusPill && (
            <div className="mt-5 inline-flex">
              <span data-testid={`app-status-pill-${appStatus}`} className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.24em] ${statusPill.cls}`}>
                {statusPill.label}
              </span>
            </div>
          )}
        </section>

        {/* Journey */}
        <section className="mt-10">
          <JourneyTimeline nodes={d.journey || []} />
        </section>

        {/* Cards */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ProfileCard profile={d.profile} />
          <ContractCard contract={d.contract} />
        </section>

        <section className="mt-6">
          <SupportCard email={d.support_email || "admin@omertabella.com"} />
        </section>

        {/* Explicit sign-out at the bottom for mobile/reach */}
        <div className="mt-12 border-t border-brand-text/10 pt-6">
          <button
            data-testid="creator-footer-signout"
            onClick={onSignOut}
            className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45 hover:text-brand-gold"
          >
            ← Sign out
          </button>
        </div>
      </div>

      <BrandFooter />
    </main>
  );
}
